import { useEffect, useMemo, useRef, useState } from 'react';

import { ContactProps } from '@/models/Contact';
import { PartnerProps } from '@/models/Partner';
import { USCountryId } from '@/utils/constants';

type Args = {
  partnerGroupId?: number | null;
  primaryContactId?: number | null;
  getPartnersByPartnerGroupId: (partnerGroupId: number) => Promise<PartnerProps[]>;
  getAllContactsByPartnerId: (partnerId: number) => Promise<ContactProps[]>;
};

type Result = {
  options: ContactProps[];
  loading: boolean;
  error: unknown | null;
  refresh: () => void;
};

function isFullAddress(contact: ContactProps): boolean {
  if (!contact.countryId) return false;

  const base = !!(contact.streetAddress1 && contact.city && contact.zipCode);

  if (contact.countryId !== USCountryId) return base;

  return base && !!(contact.stateId && contact.countyId);
}

function sortByLastNameThenFirst(a: ContactProps, b: ContactProps): number {
  const aLast = (a.lastName ?? '').toLowerCase();
  const bLast = (b.lastName ?? '').toLowerCase();
  if (aLast < bLast) return -1;
  if (aLast > bLast) return 1;

  const aFirst = (a.firstName ?? '').toLowerCase();
  const bFirst = (b.firstName ?? '').toLowerCase();
  if (aFirst < bFirst) return -1;
  if (aFirst > bFirst) return 1;

  const aEmail = (a.email ?? '').toLowerCase();
  const bEmail = (b.email ?? '').toLowerCase();
  if (aEmail < bEmail) return -1;
  if (aEmail > bEmail) return 1;

  return 0;
}

function uniqueByIdOrEmail(contacts: ContactProps[]): ContactProps[] {
  const seenIds = new Set<number>();
  const seenEmails = new Set<string>();
  const out: ContactProps[] = [];

  for (const c of contacts) {
    const id = c.id ?? null;
    const email = (c.email ?? '').trim().toLowerCase();

    const dupById = id != null && seenIds.has(id);
    const dupByEmail = email && seenEmails.has(email);
    if (dupById || dupByEmail) continue;

    if (id != null) seenIds.add(id);
    if (email) seenEmails.add(email);

    out.push(c);
  }

  return out;
}

export function usePrimaryContactOptions({
  partnerGroupId,
  primaryContactId,
  getPartnersByPartnerGroupId,
  getAllContactsByPartnerId,
}: Args): Result {
  const [options, setOptions] = useState<ContactProps[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<unknown | null>(null);

  const [refreshKey, setRefreshKey] = useState(0);
  const refresh = () => setRefreshKey((k) => k + 1);

  const runIdRef = useRef(0);

  const effectivePartnerGroupId = useMemo(() => partnerGroupId ?? undefined, [partnerGroupId]);

  useEffect(() => {
    if (!effectivePartnerGroupId) {
      setOptions([]);
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;
    const runId = ++runIdRef.current;

    async function load() {
      setLoading(true);
      setError(null);

      try {
        const partners = await getPartnersByPartnerGroupId(effectivePartnerGroupId);
        if (cancelled || runId !== runIdRef.current) return;

        const partnerIds = (partners ?? []).map((p) => p.id).filter((id): id is number => !!id);

        if (partnerIds.length === 0) {
          setOptions([]);
          setLoading(false);
          return;
        }

        const contactLists = await Promise.all(
          partnerIds.map((id) => getAllContactsByPartnerId(id))
        );
        if (cancelled || runId !== runIdRef.current) return;

        const allContacts = contactLists.flat().filter(Boolean);

        const primary = primaryContactId
          ? allContacts.filter((c) => c.id === primaryContactId)
          : [];
        const eligible = allContacts.filter((c) => {
          if (primaryContactId && c.id === primaryContactId) return true;
          return isFullAddress(c);
        });

        const deduped = uniqueByIdOrEmail([...primary, ...eligible]).sort(sortByLastNameThenFirst);

        setOptions(deduped);
        setLoading(false);
      } catch (e) {
        if (cancelled || runId !== runIdRef.current) return;
        setError(e);
        setOptions([]);
        setLoading(false);
      }
    }

    load();

    return () => {
      cancelled = true;
    };
  }, [
    effectivePartnerGroupId,
    primaryContactId,
    getPartnersByPartnerGroupId,
    getAllContactsByPartnerId,
    refreshKey,
  ]);

  return { options, loading, error, refresh };
}
